import type { AppMode } from '@/stores/app-mode';

/**
 * LA TABLE DES ROUTES DU SITE : source unique du routeur, des gardes et de la
 * vérification de complétude (`npm run audit:coverage`).
 *
 * Chaque route du site dit :
 *   - quel écran de l'application mobile elle remplace (`mobile`, chemin du
 *     fichier sous `bul-mobile/src/app`, sans `.tsx`) : c'est ce qui permet de
 *     vérifier qu'AUCUN écran du mobile n'a été oublié ;
 *   - la feature qui la porte et le fichier de page (`features/<feature>/pages/<page>.tsx`) ;
 *   - qui peut l'ouvrir (`access`), dans quel habillage (`layout`), et, pour
 *     les espaces de travail, dans quels espaces (`modes`).
 *
 * Une page absente du disque s'affiche en « écran en construction » : le
 * routeur ne casse jamais, et l'audit la signale.
 */
export type RouteAccess =
  /** Ouverte à tous, connecté ou non (vérification d'un reçu). */
  | 'public'
  /** Réservée aux visiteurs non connectés (connexion, inscription). */
  | 'guest'
  /** Réservée aux comptes connectés. */
  | 'private';

export type RouteLayout =
  /** Écran d'entrée : marque à gauche sur ordinateur, formulaire seul sur téléphone. */
  | 'auth'
  /** Racine d'onglet : barre latérale sur ordinateur, barre du haut et onglets en bas sur téléphone. */
  | 'tab'
  /** Page poussée : barre latérale sur ordinateur, en-tête avec retour sur téléphone, sans onglets. */
  | 'stack'
  /** Scène plein écran (fil vidéo, direct) : sombre, sans onglets sur téléphone. */
  | 'immersive'
  /** Aucun habillage (retour de Google). */
  | 'bare';

export type RouteEntry = {
  path: string;
  feature: string;
  page: string;
  mobile: ReadonlyArray<string>;
  access: RouteAccess;
  layout: RouteLayout;
  /** Espaces où la route a un sens. Absent : tous. */
  modes?: ReadonlyArray<AppMode>;
  /** Encore ouverte à un compte désactivé (commandes, litiges, discussions, réglages). */
  suspended?: boolean;
};

const guest = (path: string, page: string, mobile: Array<string>): RouteEntry => ({
  path,
  feature: 'auth',
  page,
  mobile,
  access: 'guest',
  layout: 'auth',
});

export const ROUTES: ReadonlyArray<RouteEntry> = [
  // ------------------------------------------------------------- entrée
  guest('/welcome', 'WelcomePage', ['(auth)/welcome']),
  guest('/sign-in', 'SignInPage', ['(auth)/sign-in']),
  guest('/register', 'RegisterPage', ['(auth)/register']),
  guest('/verify', 'VerifyCodePage', ['(auth)/verify']),
  guest('/google-register', 'GoogleRegisterPage', ['(auth)/google-register']),
  guest('/google-link', 'GoogleLinkPage', ['(auth)/google-link']),
  {
    path: '/auth/google/callback',
    feature: 'auth',
    page: 'GoogleCallbackPage',
    mobile: [],
    access: 'public',
    layout: 'bare',
  },
  {
    path: '/account-suspended',
    feature: 'auth',
    page: 'SuspendedAccountPage',
    mobile: ['account-suspended'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/verify-receipt',
    feature: 'orders',
    page: 'VerifyReceiptPage',
    mobile: ['verify-receipt', '(auth)/verify-receipt'],
    access: 'public',
    layout: 'stack',
  },

  // ------------------------------------------------------------- place de marché
  {
    path: '/',
    feature: 'feed',
    page: 'HomePage',
    mobile: ['(tabs)/index', 'index'],
    access: 'private',
    layout: 'tab',
    modes: ['buyer'],
  },
  {
    path: '/feed',
    feature: 'feed',
    page: 'ForYouPage',
    mobile: ['feed'],
    access: 'private',
    layout: 'immersive',
    modes: ['buyer'],
  },
  {
    path: '/listing/:id',
    feature: 'feed',
    page: 'ListingDetailPage',
    mobile: ['listing/[id]/index'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/explore',
    feature: 'catalog',
    page: 'ExplorePage',
    mobile: ['(tabs)/explore'],
    access: 'private',
    layout: 'tab',
    modes: ['buyer'],
  },
  {
    path: '/product/:id',
    feature: 'catalog',
    page: 'ProductPage',
    mobile: ['product/[id]/index'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/publish',
    feature: 'publish',
    page: 'PublishPage',
    mobile: ['(tabs)/publish'],
    access: 'private',
    layout: 'tab',
    modes: ['buyer', 'seller'],
  },
  {
    path: '/urgent-request/new',
    feature: 'urgent-requests',
    page: 'CreateUrgentRequestPage',
    mobile: ['urgent-request/new'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/urgent-request/:id',
    feature: 'urgent-requests',
    page: 'UrgentRequestPage',
    mobile: ['urgent-request/[id]/index'],
    access: 'private',
    layout: 'stack',
  },

  // ------------------------------------------------------------- compte
  {
    path: '/menu',
    feature: 'account',
    page: 'MenuPage',
    mobile: ['menu'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/settings',
    feature: 'account',
    page: 'SettingsPage',
    mobile: ['settings'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/kyc',
    feature: 'account',
    page: 'KycPage',
    mobile: ['kyc'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/payment-accounts',
    feature: 'account',
    page: 'PaymentAccountsPage',
    mobile: ['payment-accounts'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/agent-application',
    feature: 'account',
    page: 'AgentApplicationPage',
    mobile: ['agent-application'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/bookmarks',
    feature: 'account',
    page: 'BookmarksPage',
    mobile: ['bookmarks'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/shops/followed',
    feature: 'account',
    page: 'FollowedShopsPage',
    mobile: ['shops/followed'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/profile/:id',
    feature: 'account',
    page: 'PublicProfilePage',
    mobile: ['profile/[id]'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/notifications',
    feature: 'notifications',
    page: 'NotificationsPage',
    mobile: ['notifications'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },

  // ------------------------------------------------------------- boutiques
  {
    path: '/shop/new',
    feature: 'shops',
    page: 'CreateShopPage',
    mobile: ['shop/new'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/shop/:id',
    feature: 'shops',
    page: 'ShopPage',
    mobile: ['shop/[id]/index'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/shop/:id/edit',
    feature: 'shops',
    page: 'EditShopPage',
    mobile: ['shop/[id]/edit'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/shop/:id/profile',
    feature: 'shops',
    page: 'ShopProfilePage',
    mobile: ['shop/[id]/profile'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/shop/:id/members',
    feature: 'shops',
    page: 'ShopMembersPage',
    mobile: ['shop/[id]/members'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/shop/:id/subscription',
    feature: 'seller',
    page: 'SubscriptionPage',
    mobile: ['shop/[id]/subscription'],
    access: 'private',
    layout: 'stack',
  },

  // ------------------------------------------------------------- espace vendeur
  {
    path: '/seller',
    feature: 'seller',
    page: 'SellerDashboardPage',
    mobile: ['(tabs)/seller-dashboard', 'seller/index'],
    access: 'private',
    layout: 'tab',
    modes: ['seller'],
  },
  {
    path: '/seller/products',
    feature: 'seller',
    page: 'CatalogPage',
    mobile: ['(tabs)/seller-products'],
    access: 'private',
    layout: 'tab',
    modes: ['seller'],
  },
  {
    path: '/seller/orders',
    feature: 'orders',
    page: 'SellerOrdersPage',
    mobile: ['(tabs)/seller-orders'],
    access: 'private',
    layout: 'tab',
    modes: ['seller'],
  },
  {
    path: '/seller/finances',
    feature: 'seller',
    page: 'FinancesPage',
    mobile: ['seller/finances'],
    access: 'private',
    layout: 'stack',
    modes: ['seller'],
  },
  {
    path: '/seller/billing',
    feature: 'seller',
    page: 'BillingPage',
    mobile: ['seller/billing'],
    access: 'private',
    layout: 'stack',
    modes: ['seller'],
  },
  {
    path: '/seller/audience',
    feature: 'seller',
    page: 'AudiencePage',
    mobile: ['seller/audience'],
    access: 'private',
    layout: 'stack',
    modes: ['seller'],
  },
  {
    path: '/seller/counter-sale',
    feature: 'seller',
    page: 'CounterSalePage',
    mobile: ['seller/counter-sale'],
    access: 'private',
    layout: 'stack',
    modes: ['seller'],
  },
  {
    path: '/seller/stock',
    feature: 'stock',
    page: 'StockPage',
    mobile: ['seller/stock/index'],
    access: 'private',
    layout: 'stack',
    modes: ['seller'],
  },
  {
    path: '/seller/stock/:productId',
    feature: 'stock',
    page: 'ProductStockPage',
    mobile: ['seller/stock/[productId]'],
    access: 'private',
    layout: 'stack',
    modes: ['seller'],
  },
  {
    path: '/seller/live/new',
    feature: 'live',
    page: 'CreateLivePage',
    mobile: ['seller/live/new'],
    access: 'private',
    layout: 'stack',
    modes: ['seller'],
  },
  {
    path: '/seller/live/:id',
    feature: 'live',
    page: 'LiveHostPage',
    mobile: ['seller/live/[id]'],
    access: 'private',
    layout: 'immersive',
    modes: ['seller'],
  },
  {
    path: '/product/new',
    feature: 'seller',
    page: 'CreateProductPage',
    mobile: ['product/new'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/product/:id/edit',
    feature: 'seller',
    page: 'EditProductPage',
    mobile: ['product/[id]/edit'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/listing/new',
    feature: 'seller',
    page: 'CreateListingPage',
    mobile: ['listing/new'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/listing/:id/edit',
    feature: 'seller',
    page: 'EditListingPage',
    mobile: ['listing/[id]/edit'],
    access: 'private',
    layout: 'stack',
  },

  // ------------------------------------------------------------- achats
  {
    path: '/cart',
    feature: 'cart',
    page: 'CartPage',
    mobile: ['(tabs)/cart'],
    access: 'private',
    layout: 'tab',
    modes: ['buyer'],
  },
  {
    path: '/checkout',
    feature: 'orders',
    page: 'CheckoutPage',
    mobile: ['checkout'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/orders',
    feature: 'orders',
    page: 'OrdersPage',
    mobile: ['orders'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/order/:id',
    feature: 'orders',
    page: 'OrderDetailPage',
    mobile: ['order/[id]/index'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/order/:id/declare',
    feature: 'orders',
    page: 'DeclarePaymentPage',
    mobile: ['order/[id]/declare'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/order/:id/reason',
    feature: 'orders',
    page: 'RefuseReasonPage',
    mobile: ['order/[id]/reason'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/order/:id/receipt',
    feature: 'orders',
    page: 'ReceiptPage',
    mobile: ['order/[id]/receipt'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/payments/:id',
    feature: 'orders',
    page: 'CheckoutPaymentsPage',
    mobile: ['payments/[id]'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/disputes',
    feature: 'disputes',
    page: 'DisputesPage',
    mobile: ['disputes'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/dispute/:id',
    feature: 'disputes',
    page: 'DisputePage',
    mobile: ['dispute/[id]'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },

  // ------------------------------------------------------------- messagerie
  {
    path: '/messages',
    feature: 'messaging',
    page: 'ConversationsPage',
    mobile: ['(tabs)/messages'],
    access: 'private',
    layout: 'tab',
    modes: ['buyer', 'seller', 'agent'],
  },
  {
    path: '/conversation/new',
    feature: 'messaging',
    page: 'ContactPage',
    mobile: ['conversation/new'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/conversation/new-group',
    feature: 'messaging',
    page: 'NewGroupPage',
    mobile: ['conversation/new-group'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/conversation/share',
    feature: 'messaging',
    page: 'ShareListingPage',
    mobile: ['conversation/share'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/conversation/:id',
    feature: 'messaging',
    page: 'ConversationPage',
    mobile: ['conversation/[id]/index'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/conversation/:id/info',
    feature: 'messaging',
    page: 'GroupInfoPage',
    mobile: ['conversation/[id]/info'],
    access: 'private',
    layout: 'stack',
    suspended: true,
  },
  {
    path: '/call',
    feature: 'calls',
    page: 'CallPage',
    mobile: ['call/index'],
    access: 'private',
    layout: 'immersive',
    suspended: true,
  },
  {
    path: '/call/:id',
    feature: 'calls',
    page: 'CallPage',
    mobile: ['call/[id]'],
    access: 'private',
    layout: 'immersive',
    suspended: true,
  },
  {
    path: '/conversation/:id/add',
    feature: 'messaging',
    page: 'AddMembersPage',
    mobile: ['conversation/[id]/add'],
    access: 'private',
    layout: 'stack',
  },

  // ------------------------------------------------------------- communautés
  {
    path: '/communities',
    feature: 'communities',
    page: 'CommunitiesPage',
    mobile: ['communities'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/community/new',
    feature: 'communities',
    page: 'CreateCommunityPage',
    mobile: ['community/new'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/community/:id',
    feature: 'communities',
    page: 'CommunityPage',
    mobile: ['community/[id]/index'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/community/:id/compose',
    feature: 'communities',
    page: 'CommunityComposePage',
    mobile: ['community/[id]/compose'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/community/:id/members',
    feature: 'communities',
    page: 'CommunityMembersPage',
    mobile: ['community/[id]/members'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/community/:id/moderation',
    feature: 'communities',
    page: 'CommunityModerationPage',
    mobile: ['community/[id]/moderation'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/community/:id/post/:postId',
    feature: 'communities',
    page: 'CommunityPostPage',
    mobile: ['community/[id]/post/[postId]'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/community-post/:id',
    feature: 'communities',
    page: 'CommunityPostPage',
    mobile: ['community-post/[id]'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/community-share',
    feature: 'communities',
    page: 'ShareToCommunityPage',
    mobile: ['community-share'],
    access: 'private',
    layout: 'stack',
  },

  // ------------------------------------------------------------- directs
  {
    path: '/lives',
    feature: 'live',
    page: 'LivesPage',
    mobile: ['lives'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/live/:id',
    feature: 'live',
    page: 'LiveViewerPage',
    mobile: ['live/[id]'],
    access: 'private',
    layout: 'immersive',
  },

  // ------------------------------------------------------------- certification et agents
  {
    path: '/certification-request',
    feature: 'inspections',
    page: 'CertificationRequestPage',
    mobile: ['certification-request'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/inspections',
    feature: 'inspections',
    page: 'InspectionsPage',
    mobile: ['inspections'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/inspection/:id',
    feature: 'inspections',
    page: 'InspectionPage',
    mobile: ['inspection/[id]'],
    access: 'private',
    layout: 'stack',
  },
  {
    path: '/agent/missions',
    feature: 'inspections',
    page: 'AgentMissionsPage',
    mobile: ['(tabs)/agent-missions'],
    access: 'private',
    layout: 'tab',
    modes: ['agent'],
  },
  {
    path: '/agent/agenda',
    feature: 'inspections',
    page: 'AgentAgendaPage',
    mobile: ['(tabs)/agent-agenda'],
    access: 'private',
    layout: 'tab',
    modes: ['agent'],
  },

  // ------------------------------------------------------------- administration
  {
    path: '/admin',
    feature: 'admin',
    page: 'AdminDashboardPage',
    mobile: ['(tabs)/admin-dashboard', 'admin/index'],
    access: 'private',
    layout: 'tab',
    modes: ['admin'],
  },
  {
    path: '/admin/moderation',
    feature: 'admin',
    page: 'ModerationPage',
    mobile: ['(tabs)/admin-moderation'],
    access: 'private',
    layout: 'tab',
    modes: ['admin'],
  },
  {
    path: '/admin/disputes',
    feature: 'disputes',
    page: 'AdminDisputesPage',
    mobile: ['(tabs)/admin-disputes'],
    access: 'private',
    layout: 'tab',
    modes: ['admin'],
  },
  {
    path: '/admin/finances',
    feature: 'admin',
    page: 'AdminFinancesPage',
    mobile: ['(tabs)/admin-finances', 'admin/finances'],
    access: 'private',
    layout: 'tab',
    modes: ['admin'],
  },
  {
    path: '/admin/accounts',
    feature: 'admin',
    page: 'AccountsPage',
    mobile: ['admin/accounts'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/account/:id',
    feature: 'admin',
    page: 'AccountModerationPage',
    mobile: ['admin/account/[id]'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/agents',
    feature: 'admin',
    page: 'AgentApplicationsPage',
    mobile: ['admin/agents'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/audit',
    feature: 'admin',
    page: 'AuditLogPage',
    mobile: ['admin/audit'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/communities',
    feature: 'admin',
    page: 'AdminCommunitiesPage',
    mobile: ['admin/communities'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/kyc',
    feature: 'admin',
    page: 'KycQueuePage',
    mobile: ['admin/kyc'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/reports',
    feature: 'admin',
    page: 'ReportsPage',
    mobile: ['admin/reports'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/sanctions',
    feature: 'admin',
    page: 'SanctionsPage',
    mobile: ['admin/sanctions'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/sectors',
    feature: 'admin',
    page: 'AdminSectorsPage',
    mobile: ['admin/sectors'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/categories',
    feature: 'admin',
    page: 'AdminCategoriesPage',
    mobile: ['admin/categories'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/category/:id',
    feature: 'admin',
    page: 'AdminCategoryPage',
    mobile: ['admin/category/[id]'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },
  {
    path: '/admin/settings',
    feature: 'admin',
    page: 'PlatformSettingsPage',
    mobile: ['admin/settings'],
    access: 'private',
    layout: 'stack',
    modes: ['admin'],
  },

  // ------------------------------------------------------------- charte
  {
    path: '/design-system',
    feature: 'design-system',
    page: 'DesignSystemPage',
    mobile: ['design-system'],
    access: 'private',
    layout: 'stack',
  },
];

/**
 * Les fichiers du mobile qui ne sont pas des écrans mais l'habillage : ils ont
 * leur équivalent dans l'ossature du site, pas dans une page.
 */
export const SHELL_EQUIVALENTS: Readonly<Record<string, string>> = {
  _layout: 'src/app/RootLayout.tsx (fournisseurs, garde de session, pastille hors ligne)',
  '(auth)/_layout': 'src/app/shell/AuthLayout.tsx (espace public, redirige un compte connecté)',
  '(tabs)/_layout': 'src/app/shell/AppShell.tsx (navigation par espace, pastilles de compteurs)',
};
